<template>
  <div>
    <aw-expire-timeframe-modal />
    <aw-cold-parcel-expire-timeframe-modal />
    <aw-gdpr-acceptance-modal v-if="isGdprOpened" :key="`gdpr-acceptance-modal-${isGdprOpened}`" />
    <aw-static-content-modal v-if="isStaticContentModalOpened" />
    <aw-profile-modal v-if="isProfileModalOpened" />
    <aw-cart-merge-modal v-if="isCartMergeOpened" :key="`cart-merge-modal-modal-${isCartMergeOpened}`" />
    <aw-delivery-method-modal v-if="isDeliveryMethodOpened" :key="`delivery-modal-${isDeliveryMethodOpened}`" />
    <aw-start-popup v-if="isStartupPopupOpened" />
  </div>
</template>

<script setup>
  import { computed, defineAsyncComponent, onMounted, watch } from 'vue';
  import { useNuxtApp } from 'nuxt/app';
  import { storeToRefs } from 'pinia';
  import { useModalStore } from '~~/common/stores/modal.js';
  import { M_CART_MERGE, M_DELIVERY_METHOD, M_GDPR, M_PROFILE, M_START_POPUP, M_STATIC_CONTENT } from '~~/common/config/Modal';
  import { useConfigStore } from '~~/common/stores/config.js';
  import { useDeliveryStore } from '~~/shop/app/stores/delivery.js';
  import { getStartPopupImage } from '~~/shop/app/config/StartPopupConfig';

  const AwCartMergeModal = defineAsyncComponent(() => import('~~/common/components/Common/Modals/AwCartMergeModal'));
  const AwProfileModal = defineAsyncComponent(() => import('~~/shop/app/components/Common/Modals/AwProfileModal'));
  const AwGdprAcceptanceModal = defineAsyncComponent(() => import('~~/shop/app/components/Common/Modals/AwGdprAcceptanceModal'));
  const AwStaticContentModal = defineAsyncComponent(() => import('~~/shop/app/components/Common/Modals/AwStaticContentModal'));
  const AwExpireTimeframeModal = defineAsyncComponent(() => import('~~/shop/app/components/Common/Modals/AwExpireTimeframeModal'));
  const AwColdParcelExpireTimeframeModal = defineAsyncComponent(() => import('~~/shop/app/components/Common/Modals/AwColdParcelExpireTimeframeModal'));
  const AwDeliveryMethodModal = defineAsyncComponent(() => import('~~/shop/app/components/Common/Modals/AwDeliveryMethodAndTimeframeModal'));
  const AwStartPopup = defineAsyncComponent(() => import('~~/shop/app/components/Common/Modals/AwStartPopup'));

  const modalStore = useModalStore();
  const deliveryStore = useDeliveryStore();

  const {
    $cookies,
  } = useNuxtApp();

  async function preloadStartPopupImage (popup) {
    const src = getStartPopupImage(popup);
    if (!src) {
      return;
    }
    const image = new Image();
    image.src = src;
    try {
      await image.decode();
    } catch {
      // A broken image must not keep the popup from opening.
    }
  }

  const { activeGenericModal } = storeToRefs(modalStore);
  const { setup: deliverySetup } = storeToRefs(deliveryStore);

  const isCartMergeOpened = computed(() => activeGenericModal.value.type === M_CART_MERGE);
  const isDeliveryMethodOpened = computed(() => activeGenericModal.value.type === M_DELIVERY_METHOD);
  const isGdprOpened = computed(() => activeGenericModal.value.type === M_GDPR);
  const isStaticContentModalOpened = computed(() => activeGenericModal.value.type === M_STATIC_CONTENT);
  const isProfileModalOpened = computed(() => activeGenericModal.value.type === M_PROFILE);
  const isStartupPopupOpened = computed(() => activeGenericModal.value.type === M_START_POPUP);
  const startPopup = computed(() => useConfigStore().configCommon?.startPopup);

  onMounted(async () => {
    if (!$cookies.get('startup_popup_closed') && Boolean(startPopup.value)) {
      // Decode the image before opening so the modal is laid out at its final
      // size instead of growing from min-height once the image arrives.
      await preloadStartPopupImage(startPopup.value);
      modalStore.openStartPopup();
    }
  });

  // check delivery area is changed -> if changed, then save laterOpen flag. If close modal we check this.
  watch(deliverySetup, newValue => {
    // if change delivery area/store
    if (newValue) {
      modalStore.setAlertModalLaterOpen();
    }
  });

</script>
